import { Summary } from '@maximizer/ai/ui';
import { OutlookTimelineSummaryArgs } from '@maximizer/outlook/shared/domain';

export const TIMELINE_SUMMARY_ARGS_NO_FILTER: OutlookTimelineSummaryArgs = {
  key: 'story-entry-1',
  filter: {
    interactions: [],
    users: [],
  },
};

export const TIMELINE_SUMMARY_ARGS_WITH_FILTER: OutlookTimelineSummaryArgs = {
  ...TIMELINE_SUMMARY_ARGS_NO_FILTER,
  filter: {
    interactions: ['Email'],
    users: ['MASTER'],
  },
};

export const TIMELINE_EVENTS = [
  {
    type: 'Email',
    subject: 'Outlook Add-in testing follow-up',
    description:
      'The team discussed validating the new health-check flow and a sync toggle issue affecting the Outlook add-in.',
    date: {
      display: '2026-03-10 4:25 a.m.',
    },
    creator: {
      display: 'Ankit Priyadarshi',
    },
    entriesDisplay: 'Megan Krenbrink',
  },
  {
    type: 'Note',
    subject: 'AI Hub card alignment',
    description:
      'The existing AI Hub structure should be reused for Outlook-specific experiences with updated terminology.',
    date: {
      display: '2026-03-05 3:08 p.m.',
    },
    creator: {
      display: 'Elena Zakharova',
    },
    entriesDisplay: 'Ankit Priyadarshi',
  },
];

export const SUMMARY_MOCK: Summary = {
  dealContext: [
    'The communications revolve around various development and testing tasks for Microsoft Azure DevOps projects.',
  ],
  keyMilestoneAndTimelines: [
    {
      dateTime: '2026-03-02 9:57 a.m.',
      content:
        'Andre Kampmann mentioned Ankit Priyadarshi regarding extending the rolling window for Outlook Sync to one year ahead.',
    },
    {
      dateTime: '2026-03-10 4:25 a.m.',
      content:
        'Testing of the health-check API uncovered a sync toggle issue in the Outlook Add-in.',
    },
  ],
  highlightsOfPastCommunications: [
    {
      theme: 'Technical requirements and testing',
      items: [
        'Discussions focused on Outlook Sync date range behavior and regression testing.',
        'The team investigated issues affecting previewing files and sync controls in the add-in.',
      ],
    },
    {
      theme: 'Feature development',
      items: [
        'Existing AI Hub card patterns were aligned for Outlook-specific scenarios.',
      ],
    },
  ],
  recentCommunicationsSummary: [
    {
      dateTime: '2026-03-10 4:25 a.m.',
      content:
        'Ankit Priyadarshi reported that sync toggles failed to start the sync cycle during health-check API validation.',
    },
    {
      dateTime: '2026-03-05 3:08 p.m.',
      content:
        'Elena Zakharova clarified how existing AI Hub structures should be reused for Outlook-specific experiences.',
    },
  ],
  outstandingItemsAndRisks: {
    outstanding: [
      'Validate the fix for the sync toggle issue across the affected Outlook environments.',
      'Confirm deployment readiness after the health-check changes are completed.',
    ],
    risks: [
      'Unresolved sync issues could delay rollout of the Outlook Add-in improvements.',
    ],
  },
  recommendations: [
    {
      type: 'Task',
      title: 'Validate sync toggle behavior',
      content:
        'Run follow-up verification for the sync toggle issue in the same environments used for health-check testing.',
    },
    {
      type: 'Meeting',
      title: 'Review Outlook AI alignment',
      content:
        'Confirm the Outlook-specific AI Hub implementation details and terminology before the next delivery.',
    },
  ],
  sentimentAnalysis: [
    'The tone is collaborative and focused on resolving technical issues quickly.',
    'There is moderate urgency around stabilizing the Outlook Add-in experience before rollout.',
  ],
};

export const RESULTS = `
<div style="font-family: Roboto, sans-serif; font-size: 0.875rem; line-height: 1.3125rem; letter-spacing: 0.35px;">
<strong>Deal context</strong>
<br/>
The communications revolve around various development and testing tasks for Microsoft Azure DevOps projects. The primary goals include implementing new features, fixing bugs, conducting regression tests, and ensuring smooth deployment of updates. Key stakeholders involved are Ankit Priyadarshi, Megan Krenbrink, Andre Kampmann, Elena Zakharova, Dhiraj Kumar, and other team members.
<br/><br/>
<strong>Key Milestones & Timelines</strong>
<ol>
  <li><p>2026-03-02 9:57 a.m.:</p><p>Andre Kampmann mentioned Ankit Priyadarshi in User Story 149019 regarding extending the rolling window for Outlook Sync to one year ahead.</p></li>
  <li><p>2026-03-02 2:26 p.m.:</p><p>Elena Zakharova mentioned Ankit Priyadarshi in User Story 152028 about previewing files in Maximizer Timeline and reported an issue with the expand button.</p></li>
  <li><p>2026-03-03 6:35 p.m.:</p><p>Bug 164800 was assigned to Ankit Priyadarshi to address users being logged out of the Outlook Add-in without reason.</p></li>
  <li><p>2026-03-05 3:08 p.m.:</p><p>Elena Zakharova mentioned Ankit Priyadarshi in User Story 159555 regarding AI Hub cards and toggle for Outlook-specific features.</p></li>
  <li><p>2026-03-10 4:25 a.m.:</p><p>Ankit Priyadarshi mentioned Megan Krenbrink in User Story 159553 about testing the health-check API for the Outlook Add-in and reported a new issue with sync toggles.</p></li>
</ol>
<br/><br/>
<strong>Highlights of Past Communications</strong>
<ol>
  <li><p>Technical Requirements and Testing</p><ul><li><p>Discussions on extending the rolling window for Outlook Sync to one year ahead.</p></li><li><p>Testing and resolving issues with previewing files in Maximizer Timeline.</p></li><li><p>Addressing bugs related to OAuth errors and user logouts in the Outlook Add-in.</p></li></ul></li>
  <li><p>Feature Development</p><ul><li><p>Development of AI Hub cards and toggles for Outlook-specific features.</p></li><li><p>Adding health-check functionality via the API for the Outlook Add-in.</p></li></ul></li>
  <li><p>Deployment and Approvals</p><ul><li><p>Multiple build and deployment approvals for various projects, including Maximizer-Call-Data-Hub, SurveyMonkey, and MS Teams Calling.</p></li></ul></li>
</ol>
<br/><br/>
<strong>Recent Communications Summary</strong>
<ol>
  <li><p>2026-03-10 4:25 a.m.:</p><p>Ankit Priyadarshi reported a new issue during testing of the health-check API for the Outlook Add-in, where sync toggles failed to start the sync cycle and displayed an error message.</p></li>
  <li><p>2026-03-05 3:08 p.m.:</p><p>Elena Zakharova clarified the reuse of existing AI Hub card structures for Outlook-specific features and provided terminology adjustments for data types.</p></li>
</ol>
<br/><br/>
<strong>Outstanding Items & Risks</strong>
<p><strong>Outstanding Items:</strong></p>
<ul>
  <li><p>Validation of the fix for the new issue reported in User Story 159553 regarding sync toggles not starting the sync cycle.</p></li>
  <li><p>Pending approvals for multiple deployment stages across various projects.</p></li>
</ul>
<p><strong>Risks:</strong></p>
<ul>
  <li><p>Potential delays in deployment due to pending approvals.</p></li>
  <li><p>Unresolved technical issues, such as the sync toggle error and OAuth-related bugs, could impact project timelines.</p></li>
</ul>
<br/><br/>
<strong>Recommended Actions</strong>
<ol>
  <li><p>Validate Sync Toggle Issue</p><p>Ensure the new issue reported in User Story 159553 is resolved and tested across all relevant databases.</p></li>
  <li><p>Deployment Approval Coordination</p><p>Schedule a meeting with all approvers to expedite pending deployment approvals for various projects.</p></li>
  <li><p>Follow-Up on OAuth Bug</p><p>Send a follow-up email to Andre Kampmann and Elena Zakharova to confirm the status of Bug 164800 and any additional steps required.</p></li>
</ol>
<br/><br/>
<strong>Sentiment Analysis</strong>
<ul>
  <li><p>The tone of communications appears collaborative and focused on resolving technical issues and meeting project deadlines.</p></li>
  <li><p>There is a sense of urgency in addressing bugs and completing testing for upcoming releases.</p></li>
</ul>
</div>
`;
